/** Training Request Create page. */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCreateTrainingRequest, useSubmitTrainingRequest, useTrainingCategories } from "../hooks";
import { TrainingCourseForm } from "../components/TrainingCourseForm";
import type { TrainingRequestCreateRequest } from "../types";

const EMPTY: TrainingRequestCreateRequest = {
  trainingTitle: "",
  categoryId: null,
  trainingStartDate: null,
  trainingEndDate: null,
  providerName: null,
  venue: null,
  applicantEstimatedCost: null,
  expectedOutcome: null,
};

function extractMsg(err: unknown) {
  const e = err as any;
  return e?.response?.data?.message ?? e?.message ?? "Something went wrong.";
}

export default function TrainingCourseCreatePage() {
  const navigate = useNavigate();
  const [values, setValues] = useState<TrainingRequestCreateRequest>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const categoriesQuery = useTrainingCategories();
  const create = useCreateTrainingRequest();
  const submit = useSubmitTrainingRequest();

  const busy = create.isPending || submit.isPending;

  // Backend has @NotNull on categoryId — required for both draft and submit.
  function validate() {
    if (!values.trainingTitle.trim()) { setError("Training title is required."); return false; }
    if (!values.categoryId) { setError("Please select a category."); return false; }
    return true;
  }

  async function handleSaveDraft() {
    setError(null);
    if (!validate()) return;
    try {
      const req = await create.mutateAsync(values);
      navigate(`/hr/training/${req.id}`);
    } catch (err) { setError(extractMsg(err)); }
  }

  async function handleSubmit() {
    setError(null);
    if (!validate()) return;
    try {
      const req = await create.mutateAsync(values);
      await submit.mutateAsync(req.id);
      navigate(`/hr/training/${req.id}`);
    } catch (err) { setError(extractMsg(err)); }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <button onClick={() => navigate(-1)} className="text-sm text-slate-500 hover:text-slate-800">
        ← Back
      </button>

      <div>
        <h1 className="text-2xl font-bold text-slate-900">New Training Request</h1>
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">{error}</div>
      )}


      <div className="rounded-2xl border bg-white p-5 shadow-sm">
        <TrainingCourseForm
          values={values}
          onChange={setValues}
          categories={categoriesQuery.data ?? []}
          categoriesLoading={categoriesQuery.isLoading}
          categoriesError={
            categoriesQuery.isError
              ? (categoriesQuery.error instanceof Error
                  ? categoriesQuery.error.message
                  : "Failed to load")
              : null
          }
          disabled={busy}
        />
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={handleSaveDraft}
          disabled={busy}
          className="rounded-xl border px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {create.isPending && !submit.isPending ? "Saving…" : "Save Draft"}
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={busy}
          className="rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {busy && submit.isPending ? "Submitting…" : "Submit for Approval"}
        </button>
      </div>
    </div>
  );
}
